import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { Space, BookingStatus, Guest } from '@/types'

export interface ValidationResult {
    valid: boolean
    message?: string
}

/**
 * Verifica se há conflitos de horário para um espaço
 */
export async function checkBookingConflict(
    orgId: string,
    spaceId: string,
    start: Date,
    end: Date,
    excludeBookingId?: string
): Promise<ValidationResult> {
    const bookingsRef = collection(db, 'bookings')

    // Status que bloqueiam o horário
    const blockingStatus: BookingStatus[] = ['CONFIRMED', 'PENDING_APPROVAL', 'PENDING_PAYMENT']

    const q = query(
        bookingsRef,
        where('orgId', '==', orgId),
        where('spaceId', '==', spaceId),
        where('status', 'in', blockingStatus)
    )

    const snapshot = await getDocs(q)

    for (const doc of snapshot.docs) {
        if (doc.id === excludeBookingId) continue

        const data = doc.data()
        const bStart = data.start.toDate()
        const bEnd = data.end.toDate()

        // Lógica de sobreposição: (StartA < EndB) AND (EndA > StartB)
        if (start < bEnd && end > bStart) {
            return {
                valid: false,
                message: 'Já existe uma reserva para este período.'
            }
        }
    }

    return { valid: true }
}

/**
 * Valida regras de negócio do espaço (antecedência, buffer, etc)
 */
export function validateBookingRules(
    space: Space,
    start: Date,
    _end: Date
): ValidationResult {
    const now = new Date()
    const rules = space.rules || {}

    // 1. Antecedência Mínima
    if (rules.minAdvanceHours) {
        const minTime = new Date(now.getTime() + rules.minAdvanceHours * 60 * 60 * 1000)
        if (start < minTime) {
            return {
                valid: false,
                message: `Este espaço requer no mínimo ${rules.minAdvanceHours}h de antecedência.`
            }
        }
    }

    // 2. Antecedência Máxima
    if (rules.maxAdvanceDays) {
        const maxTime = new Date(now.getTime() + rules.maxAdvanceDays * 24 * 60 * 60 * 1000)
        if (start > maxTime) {
            return {
                valid: false,
                message: `Reservas permitidas com no máximo ${rules.maxAdvanceDays} dias de antecedência.`
            }
        }
    }

    // 3. Dias da Semana Permitidos
    const dayOfWeek = start.getDay() // 0 (Dom) - 6 (Sáb)

    // Se allowedDays estiver definido como array (mesmo vazio), respeita estritamente
    if (Array.isArray(rules.allowedDays)) {
        if (!rules.allowedDays.includes(dayOfWeek)) {
            return {
                valid: false,
                message: 'Este espaço não funciona neste dia da semana.'
            }
        }
    } else {
        // Fallback Legado: valida allowWeekend apenas se allowedDays não existir
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
        if (isWeekend && rules.allowWeekend === false) {
            return {
                valid: false,
                message: 'Este espaço não está disponível para reservas nos finais de semana.'
            }
        }
    }

    // 4. Horário de funcionamento
    if (rules.operatingHours?.start && rules.operatingHours?.end) {
        const openStr = rules.operatingHours.start.trim()
        const closeStr = rules.operatingHours.end.trim()

        if (openStr && closeStr) {
            const startMinutes = start.getHours() * 60 + start.getMinutes()
            const endMinutes = _end.getHours() * 60 + _end.getMinutes()

            const [openH, openM] = openStr.split(':').map(n => parseInt(n) || 0)
            const [closeH, closeM] = closeStr.split(':').map(n => parseInt(n) || 0)

            const openTotal = openH * 60 + openM
            const closeTotal = closeH * 60 + closeM

            // Validação de intervalo:
            // O início da reserva deve ser >= abertura
            // O fim da reserva deve ser <= fechamento
            if (startMinutes < openTotal || endMinutes > closeTotal) {
                return {
                    valid: false,
                    message: `Horário inválido. O espaço funciona das ${openStr} às ${closeStr}.`
                }
            }
        }
    }

    return { valid: true }
}

export function calculateBookingPrice(
    space: Space,
    start: Date,
    end: Date,
    tierCounts?: Record<string, number>, // { "Adulto": 2, "Criança": 1 }
    guests?: Guest[] // Fallback for legacy or specific guest values
): number {
    let total = 0
    const durationMs = end.getTime() - start.getTime()
    const durationHours = durationMs / (1000 * 60 * 60)

    // 1. Space Rent (PER_SLOT)
    if (!space.pricingMode || space.pricingMode === 'PER_SLOT') {
        if (space.pricePerHour) {
            if (space.billingPeriod === 'FIXED') {
                total += space.pricePerHour
            } else {
                // Defines HOURLY as default
                total += Math.max(0, durationHours * space.pricePerHour)
            }
        }
    }

    // 2. Guest Tiers (PER_PERSON)
    if (space.pricingMode === 'PER_PERSON' && space.guestTiers) {
        let guestsTotal = 0

        // Calculate based on Tier Counts (Preferred)
        if (tierCounts) {
            space.guestTiers.forEach(tier => {
                const count = tierCounts[tier.name] || 0
                guestsTotal += count * tier.price
            })
        }

        // Apply Billing Period Modifier to Guest Total
        if (space.billingPeriod === 'HOURLY') {
            guestsTotal *= durationHours
        }

        total += guestsTotal
    }

    // 3. Custom Fees (Always added once per booking)
    if (space.customFees) {
        total += space.customFees.reduce((sum, fee) => sum + (fee.amount || 0), 0)
    }

    // 4. Legacy Guest Values (Direct value on guest object)
    // Only add if not using Tier Pricing to avoid double charging
    if (guests && guests.length > 0 && space.pricingMode !== 'PER_PERSON') {
        total += guests.reduce((sum, guest) => sum + (guest.value || 0), 0)
    }

    return total
}
